import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { lightColors, darkColors } from '../theme/tokens';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    return localStorage.getItem('theme-mode') || 'system';
  });

  const [systemDark, setSystemDark] = useState(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for system preference changes
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemDark);

  // Set data-theme attribute on html element
  useEffect(() => {
    const resolved = isDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', resolved);
  }, [isDark]);

  const setTheme = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('theme-mode', mode);
  };

  const colors = useMemo(() => isDark ? darkColors : lightColors, [isDark]);

  const value = {
    isDark,
    themeMode,
    setTheme,
    colors,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
